// Type Imports
import type { Settings } from '@core/contexts/settingsContext'
import type { Mode, SystemMode } from '@core/types'

// Config Imports
import themeConfig from '@configs/themeConfig'

export const getSettingsFromLocalStorage = (): Settings => {
  if (typeof window === 'undefined') {
    return {}
  }

  const storageKey = themeConfig.settingsCookieName
  const storageValue = localStorage.getItem(storageKey)

  try {
    return storageValue ? JSON.parse(storageValue) : {}
  } catch {
    return {}
  }
}

export const getMode = async (): Promise<Mode> => {
  const settingsStorage = getSettingsFromLocalStorage()

  // Get mode from localStorage or fallback to theme config
  const _mode = settingsStorage.mode || themeConfig.mode

  return _mode
}

export const getSystemMode = async (): Promise<SystemMode> => {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const mode = await getMode()

  // Get system preference from localStorage or default to light
  const colorPref = (localStorage.getItem('colorPref') as SystemMode) || 'light'

  return (mode === 'system' ? colorPref : mode) || 'light'
}

export const getServerMode = async (): Promise<string> => {
  const mode = await getMode()
  const systemMode = await getSystemMode()

  return mode === 'system' ? systemMode : mode
}

export const getSkin = async (): Promise<string> => {
  const settingsStorage = getSettingsFromLocalStorage()

  return settingsStorage.skin || 'default'
}

// Helper to save settings to localStorage
export const saveSettingsToLocalStorage = (settings: Partial<Settings>) => {
  if (typeof window === 'undefined') {
    return
  }

  const storageKey = themeConfig.settingsCookieName
  const currentSettings = getSettingsFromLocalStorage()
  const newSettings = { ...currentSettings, ...settings }

  localStorage.setItem(storageKey, JSON.stringify(newSettings))
}

// Helper to save system preference
export const saveSystemPreference = (preference: SystemMode) => {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem('colorPref', preference)
}
